/**
 * Document Rule Locator (issues #66, #67, #78)
 *
 * Finds the best-matching block-level element in the rendered document
 * for a given normative statement, then scrolls to and flashes it. Used by
 * the contradiction offcanvas to navigate the user to where a statement
 * appears in a multi-page document.
 *
 * Also supports persistent highlighting of all statements after extraction,
 * colored by deontic category (obligation, prohibition, permission).
 *
 * When `span_begin`/`span_end` character offsets are available, they are
 * used as the primary anchoring strategy. Otherwise we fall back to token
 * overlap scoring against block-level elements.
 */

import type { StatementDisplay } from '../gateway-service';

const FLASH_CLASS = 'rule-flash';
const HIGHLIGHT_CLASS = 'rule-highlight';
const FLASH_DURATION_MS = 3000;
const BLOCK_SELECTOR = 'p, li, blockquote, td, th, h1, h2, h3, h4, h5, h6, pre';

// Tokens shorter than this are dropped (mostly noise).
const MIN_TOKEN_LENGTH = 3;

// English-only minimal stopword set. For other languages we rely on the
// length filter; the numeric/unit tokens carry most of the signal anyway.
const STOPWORDS = new Set([
  'the',
  'a',
  'an',
  'and',
  'or',
  'but',
  'of',
  'to',
  'in',
  'on',
  'at',
  'for',
  'with',
  'by',
  'as',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'have',
  'has',
  'had',
  'do',
  'does',
  'did',
  'this',
  'that',
  'these',
  'those',
  'it',
  'its',
  'not',
  'shall',
  'must',
  'may',
  'should',
]);

/**
 * Map a deontic category string (e.g. "obligation") to a CSS modifier class.
 */
function deonticModifier(deonticCategory: string | null | undefined): string {
  if (!deonticCategory) return `${HIGHLIGHT_CLASS}--default`;
  if (deonticCategory === 'obligation') return `${HIGHLIGHT_CLASS}--duty`;
  if (deonticCategory === 'prohibition')
    return `${HIGHLIGHT_CLASS}--prohibition`;
  if (deonticCategory === 'permission') return `${HIGHLIGHT_CLASS}--permission`;
  return `${HIGHLIGHT_CLASS}--default`;
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9.]+/u)
    .filter((t) => t.length >= MIN_TOKEN_LENGTH && !STOPWORDS.has(t));
}

/**
 * Score a paragraph's match against rule tokens. Higher = better.
 *
 * Rewards unique-token overlap and gives a small bonus for verbatim
 * substring matches on numeric values, which are the most discriminating
 * tokens in a normative rule (e.g. "1.2" vs "0.9").
 */
function scoreParagraph(paraSet: Set<string>, ruleTokens: string[]): number {
  if (ruleTokens.length === 0) return 0;
  let overlap = 0;
  let numericOverlap = 0;
  for (const t of ruleTokens) {
    if (paraSet.has(t)) {
      overlap += 1;
      if (/\d/.test(t)) numericOverlap += 1;
    }
  }
  return overlap + numericOverlap; // numeric tokens count double
}

/** Pick the statement text most likely to appear verbatim in the source. */
function getSearchText(statement: StatementDisplay): string {
  return (
    statement.source_sentence?.trim() || statement.antecedent?.trim() || ''
  );
}

/**
 * The container's text nodes with the character offset where each one ends,
 * built once per highlight pass (#159): walking all text nodes again for
 * every statement made highlighting O(statements × text nodes).
 */
interface TextIndex {
  nodes: Text[];
  /** ends[i]: offset just past nodes[i]; non-decreasing. */
  ends: number[];
}

function buildTextIndex(container: HTMLElement): TextIndex {
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null
  );
  const nodes: Text[] = [];
  const ends: number[] = [];
  let charPos = 0;
  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    charPos += (node.textContent ?? '').length;
    nodes.push(node);
    ends.push(charPos);
  }
  return { nodes, ends };
}

/**
 * Find the block element at the given character offsets: the closest
 * block-level ancestor of the text node holding `spanBegin`, or null if the
 * text is shorter than `spanEnd` or that node is outside any block (the
 * whitespace between them) — it must not select the whole document wrapper
 * (#131).
 */
function findByCharOffset(
  index: TextIndex,
  spanBegin: number,
  spanEnd: number
): HTMLElement | null {
  const { nodes, ends } = index;
  if (ends.length === 0 || (ends[ends.length - 1] ?? 0) < spanEnd) return null;
  // First node ending after spanBegin (binary search over the ends).
  let lo = 0;
  let hi = ends.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((ends[mid] ?? 0) > spanBegin) hi = mid;
    else lo = mid + 1;
  }
  return (nodes[lo]?.parentElement?.closest(BLOCK_SELECTOR) ??
    null) as HTMLElement | null;
}

const normalize = (text: string): string =>
  text.replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Locate a statement by its character offsets, then check the result: the
 * offsets refer to the text sent to the gateway (paragraphs joined with blank
 * lines), the DOM has other whitespace and page dividers, so on a real PDF
 * they drift by a few characters and may land in the neighbouring block
 * (#131). The block must contain the start of the verbatim source sentence;
 * otherwise the text search decides. Without a source sentence (antecedent
 * only) there is nothing verbatim to check, and the offsets are trusted.
 */
function findByOffsetChecked(
  getIndex: () => TextIndex,
  statement: StatementDisplay
): HTMLElement | null {
  if (
    statement.span_begin == null ||
    statement.span_end == null ||
    statement.span_end <= statement.span_begin
  ) {
    return null;
  }
  const el = findByCharOffset(
    getIndex(),
    statement.span_begin,
    statement.span_end
  );
  const sentence = statement.source_sentence?.trim();
  if (!el || !sentence) return el;
  const head = normalize(sentence.split('\n')[0] ?? '').slice(0, 30);
  return normalize(el.textContent ?? '').includes(head) ? el : null;
}

/**
 * A non-empty block element with its text read once per lookup (#131):
 * matching every statement against every block re-read and re-lowercased
 * the whole document per statement. Tokens are built when first needed.
 */
interface Block {
  el: HTMLElement;
  lower: string;
  tokens?: Set<string>;
}

function collectBlocks(container: HTMLElement): Block[] {
  const blocks: Block[] = [];
  for (const el of container.querySelectorAll<HTMLElement>(BLOCK_SELECTOR)) {
    const text = el.textContent ?? '';
    if (text.trim().length > 0) blocks.push({ el, lower: text.toLowerCase() });
  }
  return blocks;
}

/**
 * Find the best matching block element for a statement in the document.
 * Returns the element or null if no match is found.
 */
function findRuleElement(
  statement: StatementDisplay,
  blocks: Block[]
): HTMLElement | null {
  const searchText = getSearchText(statement);
  if (!searchText) return null;

  // Try exact substring match first
  const needle = searchText.toLowerCase();
  for (const block of blocks) {
    if (block.lower.includes(needle)) {
      return block.el;
    }
  }

  // Fall back to token-overlap scoring
  const ruleTokens = tokenize(searchText);
  if (ruleTokens.length === 0) return null;

  let bestEl: HTMLElement | null = null;
  let bestScore = 0;
  for (const block of blocks) {
    block.tokens ??= new Set(tokenize(block.lower));
    const score = scoreParagraph(block.tokens, ruleTokens);
    if (score > bestScore) {
      bestScore = score;
      bestEl = block.el;
    }
  }
  const minScore = Math.min(3, Math.ceil(ruleTokens.length / 2));
  return bestEl && bestScore >= minScore ? bestEl : null;
}

const DEONTIC_MODIFIERS = [
  `${HIGHLIGHT_CLASS}--duty`,
  `${HIGHLIGHT_CLASS}--prohibition`,
  `${HIGHLIGHT_CLASS}--permission`,
  `${HIGHLIGHT_CLASS}--default`,
];

function removeDeonticModifiers(el: HTMLElement): void {
  el.classList.remove(...DEONTIC_MODIFIERS);
}

/**
 * Remove all persistent rule highlights from the document.
 */
export function clearRuleHighlights(): void {
  const container = document.getElementById('document-content');
  if (!container) return;

  const highlighted = container.querySelectorAll<HTMLElement>(
    `.${HIGHLIGHT_CLASS}`
  );
  for (const el of highlighted) {
    // If it's a span wrapper created by the text-node path, unwrap it.
    if (el.tagName === 'SPAN' && el.getAttribute('data-rule-iri')) {
      const parent = el.parentNode;
      if (parent) {
        while (el.firstChild) {
          parent.insertBefore(el.firstChild, el);
        }
        parent.removeChild(el);
        (parent as Element).normalize?.();
      }
    } else {
      el.classList.remove(HIGHLIGHT_CLASS, FLASH_CLASS);
      removeDeonticModifiers(el);
      el.removeAttribute('data-rule-iri');
      el.removeAttribute('data-rule-number');
    }
  }
}

/**
 * Highlight all extracted statements in the document with persistent
 * backgrounds colored by deontic category. Each highlighted element gets a
 * `data-rule-iri` and `data-rule-number` attribute for later lookup.
 *
 * @param statements - The extracted statements from a CHECK_RULES response
 * @returns Map of statement IRI to the 1-based statement number
 */
export function highlightAllStatements(
  statements: StatementDisplay[]
): Map<string, number> {
  clearRuleHighlights();

  // Always build the statement number map regardless of DOM state.
  const ruleNumbers = new Map<string, number>();
  for (let i = 0; i < statements.length; i++) {
    const statement: StatementDisplay | undefined = statements[i];
    if (!statement) continue;
    ruleNumbers.set(statement.iri, i + 1);
  }

  // Apply visual highlights to matching document elements if available.
  const container = document.getElementById('document-content');
  if (container) {
    const blocks = collectBlocks(container);
    let index: TextIndex | null = null;
    const getIndex = (): TextIndex => (index ??= buildTextIndex(container));

    for (const statement of statements) {
      if (!statement) continue;
      const statementNumber = ruleNumbers.get(statement.iri) ?? 0;

      // Try char-offset anchoring first (most precise)
      const offsetEl = findByOffsetChecked(getIndex, statement);
      if (offsetEl) {
        offsetEl.classList.add(HIGHLIGHT_CLASS);
        offsetEl.classList.add(deonticModifier(statement.deontic_category));
        offsetEl.setAttribute('data-rule-iri', statement.iri);
        offsetEl.setAttribute('data-rule-number', String(statementNumber));
        continue;
      }

      // Try block-level match (HTML documents)
      const el = blocks.length > 0 ? findRuleElement(statement, blocks) : null;
      if (el) {
        el.classList.add(HIGHLIGHT_CLASS);
        el.setAttribute('data-rule-iri', statement.iri);
        el.setAttribute('data-rule-number', String(statementNumber));
        continue;
      }

      // Fallback: wrap matching text in a persistent highlight span. That
      // splits a text node, so the index is rebuilt when next needed.
      if (highlightInTextNodes(container, statement, statementNumber)) {
        index = null;
      }
    }
  }

  return ruleNumbers;
}

/**
 * Locate the document text that best matches `statement`, scroll it into
 * view, and play a flash animation. The persistent highlight remains after
 * the flash fades. Returns true if a match was found.
 *
 * Two rendering paths to handle:
 *  - HTML documents (PDF/DOCX through docling): real block elements like
 *    `<p>`, `<li>` exist; flash the matching block as a whole.
 *  - Plain-text documents: the renderer dumps the whole content as a single
 *    text node; walk text nodes, wrap the matched substring in a temporary
 *    span, and flash that.
 */
export function findAndFlashStatement(statement: StatementDisplay): boolean {
  const container = document.getElementById('document-content');
  if (!container) return false;

  // Fast path: if the statement was already highlighted by highlightAllStatements,
  // find the existing element by its data attribute and flash it directly.
  const iri = CSS.escape(statement.iri);
  const existing = container.querySelector<HTMLElement>(
    `.${HIGHLIGHT_CLASS}[data-rule-iri="${iri}"]`
  );
  if (existing) {
    flashElement(existing, statement.deontic_category);
    return true;
  }

  // Try char-offset anchoring
  const offsetEl = findByOffsetChecked(
    () => buildTextIndex(container),
    statement
  );
  if (offsetEl) {
    flashElement(offsetEl, statement.deontic_category);
    return true;
  }

  // Slow path: search the document for a matching element.
  const searchText = getSearchText(statement);
  if (!searchText) return false;

  const blocks = collectBlocks(container);
  if (blocks.length > 0) {
    const el = findRuleElement(statement, blocks);
    if (el) {
      flashElement(el, statement.deontic_category);
      return true;
    }
  }

  return false;
}

/** Walk text nodes and apply a persistent highlight to the first match. */
function highlightInTextNodes(
  container: HTMLElement,
  statement: StatementDisplay,
  statementNumber: number
): boolean {
  const searchText = getSearchText(statement);
  if (!searchText) return false;

  const needle = searchText.toLowerCase();
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null
  );

  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    // Skip text nodes already inside a rule highlight
    if (node.parentElement?.closest(`.${HIGHLIGHT_CLASS}`)) continue;

    const text = node.textContent ?? '';
    const idx = text.toLowerCase().indexOf(needle);
    if (idx === -1) continue;

    const range = document.createRange();
    range.setStart(node, idx);
    range.setEnd(node, idx + needle.length);

    const wrapper = document.createElement('span');
    wrapper.className = HIGHLIGHT_CLASS;
    wrapper.setAttribute('data-rule-iri', statement.iri);
    wrapper.setAttribute('data-rule-number', String(statementNumber));
    try {
      range.surroundContents(wrapper);
    } catch {
      return false;
    }
    return true;
  }
  return false;
}

/**
 * Listen for clicks on highlighted rules inside the document content area
 * and invoke `handler(iri)` with the matched rule IRI. Returns a detach
 * function the caller must invoke before re-attaching or when the document
 * is swapped out.
 *
 * Interactive descendants (anchors, buttons, form controls) continue to
 * work: the handler only fires when the click's composed path has no
 * interactive ancestor between the target and the `.rule-highlight` block.
 *
 * Designed to pair with `highlightAllStatements()` (which sets data-rule-iri)
 * to drive document → offcanvas navigation (issue #72).
 */
export function attachRuleClickListener(
  container: HTMLElement,
  handler: (iri: string) => void
): () => void {
  const onClick = (ev: Event): void => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;

    // Walk from target up to the highlighted rule, bailing out early on
    // any interactive element so their native click semantics stand.
    let node: HTMLElement | null = target;
    while (node && node !== container) {
      if (
        node.tagName === 'A' ||
        node.tagName === 'BUTTON' ||
        node.tagName === 'INPUT' ||
        node.tagName === 'SELECT' ||
        node.tagName === 'TEXTAREA' ||
        node.tagName === 'LABEL'
      ) {
        return;
      }
      if (
        node.classList?.contains(HIGHLIGHT_CLASS) &&
        node.hasAttribute('data-rule-iri')
      ) {
        const iri = node.getAttribute('data-rule-iri');
        if (iri) handler(iri);
        return;
      }
      node = node.parentElement;
    }
  };

  container.addEventListener('click', onClick);
  return () => container.removeEventListener('click', onClick);
}

function flashElement(el: HTMLElement, deonticCategory?: string | null): void {
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // Ensure persistent highlight is applied
  if (!el.classList.contains(HIGHLIGHT_CLASS)) {
    el.classList.add(HIGHLIGHT_CLASS);
  }

  const modifier = deonticModifier(deonticCategory);

  // Remove any previous deontic modifier, then re-add to restart the color.
  removeDeonticModifiers(el);
  void el.offsetWidth; // force reflow so transition restarts

  // Set the deontic-type background color
  el.classList.add(modifier);

  // After the timeout, remove the modifier. The CSS transition on
  // .rule-highlight fades the background smoothly back to neutral gray.
  window.setTimeout(() => {
    el.classList.remove(modifier);
  }, FLASH_DURATION_MS);
}
